import * as jsxRuntime from "react/jsx-runtime";
import { Disclosure, Transition, Dialog as Dialog$1, Menu as Menu$1, Portal, Listbox, Tab as Tab$1 } from "@headlessui/react";
import { Fragment as Fragment$1, forwardRef, Children, useRef, useMemo, useEffect, createContext, useState, useCallback, useContext, createRef, isValidElement } from "react";
import { extendTailwindMerge } from "tailwind-merge";
import cx from "classnames";
import { cva } from "class-variance-authority";
import { CheckIcon, IndeterminateIcon, MinusIcon, PlusIcon, SearchIcon } from "@ngg/icons";
import { useFloating, autoPlacement, offset, size } from "@floating-ui/react-dom";
const index = "";
const Fragment = jsxRuntime.Fragment;
const jsx = jsxRuntime.jsx;
const jsxs = jsxRuntime.jsxs;
const customTwMerge = extendTailwindMerge({
  classGroups: {
    text: ["text-xxs"]
  }
});
const cn = (...inputs) => customTwMerge(cx(...inputs));
const DefaultIconComponent = ({ open }) => /* @__PURE__ */ jsx("span", { className: open ? "rotate-180 transform" : "", children: "∨" });
const Accordion = ({
  title,
  children,
  transitionProps = {},
  WrapperComponent = "div",
  ButtonComponent = "button",
  ContentComponent = "div",
  classNames = {},
  defaultOpen = false,
  IconComponent = DefaultIconComponent
}) => /* @__PURE__ */ jsx(Disclosure, { as: WrapperComponent, defaultOpen, className: classNames == null ? void 0 : classNames.wrapper, children: ({ open }) => /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsxs(
    Disclosure.Button,
    {
      className: cn("flex w-full justify-between rounded px-4 py-2", classNames == null ? void 0 : classNames.button),
      as: ButtonComponent,
      children: [
        typeof title === "function" ? title(open) : title,
        IconComponent ? /* @__PURE__ */ jsx(IconComponent, { open }) : null
      ]
    }
  ),
  /* @__PURE__ */ jsx(
    Transition,
    {
      as: Fragment$1,
      appear: true,
      enter: "transition-opacity duration-100 ease",
      enterFrom: "opacity-0",
      enterTo: "opacity-100",
      leave: "transition-opacity duration-100 ease",
      leaveFrom: "opacity-100",
      leaveTo: "opacity-0",
      ...transitionProps,
      children: /* @__PURE__ */ jsx(Disclosure.Panel, { as: ContentComponent, ...{ className: cn("py-2 px-4 rounded w-full", classNames == null ? void 0 : classNames.panel) }, children })
    }
  )
] }) });
const badge = cva("inline-flex items-center px-[0.5em] py-[0.5em] font-normal leading-none", {
  variants: {
    intent: { error: "", success: "", info: "", warning: "" },
    type: { normal: "", filled: "", outlined: "bg-transparent border" },
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base"
    },
    radius: {
      none: "rounded-none",
      rounded: "rounded-lg",
      full: "rounded-full"
    }
  },
  compoundVariants: [
    // Normal
    {
      intent: "error",
      type: "normal",
      className: "bg-error-light text-error-dark"
    },
    {
      intent: "success",
      type: "normal",
      className: "bg-success-light text-success-dark"
    },
    {
      intent: "info",
      type: "normal",
      className: "bg-info-light text-info-dark"
    },
    {
      intent: "warning",
      type: "normal",
      className: "bg-warning-light text-warning-dark"
    },
    // Filled
    {
      intent: "error",
      type: "filled",
      className: "bg-error text-error-contrast"
    },
    {
      intent: "success",
      type: "filled",
      className: "bg-success text-success-contrast"
    },
    {
      intent: "info",
      type: "filled",
      className: "bg-info text-info-contrast"
    },
    {
      intent: "warning",
      type: "filled",
      className: "bg-warning text-warning-contrast"
    },
    // Outlined
    {
      intent: "error",
      type: "outlined",
      className: "border-error text-error"
    },
    {
      intent: "success",
      type: "outlined",
      className: "border-success text-success"
    },
    {
      intent: "info",
      type: "outlined",
      className: "border-info text-info"
    },
    {
      intent: "warning",
      type: "outlined",
      className: "border-warning text-warning"
    }
  ]
});
const Badge = ({
  as,
  children,
  startIcon,
  className,
  endIcon,
  size: size2 = "md",
  variant = "filled",
  color,
  radius = "none",
  ...props
}) => {
  const Component = as || "span";
  return /* @__PURE__ */ jsxs(Component, { className: cn(badge({ intent: color, type: variant, radius, size: size2 }), className), ...props, children: [
    startIcon && /* @__PURE__ */ jsx("span", { className: "mr-1 inline-flex items-center", children: startIcon }),
    children,
    endIcon && /* @__PURE__ */ jsx("span", { className: "ml-1 inline-flex items-center", children: endIcon })
  ] });
};
const Breadcrumbs = ({ crumbs, as = "a", separator = "/", classNames }) => {
  const Component = as;
  return /* @__PURE__ */ jsx("nav", { "aria-label": "Breadcrumb", children: /* @__PURE__ */ jsx("ol", { className: cn("text-xxs text-grey-300 flex px-4 uppercase", classNames == null ? void 0 : classNames.breadcrumbs), children: crumbs.map(({ path, title, label }, index2) => /* @__PURE__ */ jsx(Fragment$1, { children: index2 < crumbs.length - 1 ? /* @__PURE__ */ jsxs("li", { children: [
    /* @__PURE__ */ jsx(Component, { href: path, "aria-label": title, children: label }),
    /* @__PURE__ */ jsx("span", { className: cn("mx-2", classNames == null ? void 0 : classNames.separator), "aria-hidden": true, children: separator })
  ] }) : /* @__PURE__ */ jsx("li", { className: cn("underline", classNames == null ? void 0 : classNames.currentItem), children: label }) }, path)) }) });
};
const DefaultLoadingComponent = (loadingClassName) => /* @__PURE__ */ jsxs("div", { role: "status", children: [
  /* @__PURE__ */ jsxs(
    "svg",
    {
      "aria-hidden": "true",
      className: cn("h-4 w-4 animate-spin fill-current text-white/50", loadingClassName),
      viewBox: "0 0 100 101",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z",
            fill: "currentFill"
          }
        )
      ]
    }
  ),
  /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Loading..." })
] });
const RawButton = ({
  as,
  fullWidth,
  className,
  loadingClassName,
  isLoading,
  LoadingComponent = () => DefaultLoadingComponent(loadingClassName),
  children,
  disabled = false,
  startIcon = null,
  endIcon = null,
  buttonRef,
  ...props
}) => {
  const Component = as || "button";
  return /* @__PURE__ */ jsxs(
    Component,
    {
      ref: buttonRef,
      type: Component === "button" ? "button" : void 0,
      className: cn(
        "action-focus relative inline-flex cursor-pointer items-center gap-2 aria-disabled:pointer-events-none aria-disabled:select-none aria-disabled:opacity-50",
        fullWidth && "w-full",
        className
      ),
      disabled,
      "aria-disabled": disabled,
      ...props,
      children: [
        /* @__PURE__ */ jsxs("span", { className: "flex flex-1 items-center justify-start", children: [
          isLoading && /* @__PURE__ */ jsx(LoadingComponent, {}),
          startIcon && !isLoading && startIcon
        ] }),
        children,
        /* @__PURE__ */ jsx("span", { className: "flex flex-1 items-center justify-end", children: endIcon && !isLoading && endIcon })
      ]
    }
  );
};
const Button$1 = forwardRef(
  (props, ref) => /* @__PURE__ */ jsx(RawButton, { ...props, buttonRef: ref })
);
const defaultHeader = ({ title, subtitle }) => /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("h3", { className: "text-3xl text-black", children: title }),
  subtitle ? /* @__PURE__ */ jsx("p", { className: "text-sm text-black/70", children: subtitle }) : null
] });
const defaultContent = ({ description }) => /* @__PURE__ */ jsx("p", { className: "text-base text-black", children: description });
const Card = forwardRef(
  ({
    content,
    getHeader = defaultHeader,
    getFooter = () => null,
    getContent = defaultContent,
    CardComponent = "div",
    CardHeaderComponent = "div",
    CardFooterComponent = "div",
    CardContentComponent = "div"
  }, ref) => /* @__PURE__ */ jsxs(CardComponent, { className: "rounded shadow", ref, children: [
    /* @__PURE__ */ jsx(CardHeaderComponent, { className: "flex flex-col px-4 py-2", children: getHeader(content) }),
    /* @__PURE__ */ jsx(CardContentComponent, { className: "flex flex-col p-4", children: getContent(content) }),
    getFooter(content) ? /* @__PURE__ */ jsx(CardFooterComponent, { className: "flex p-4", children: getFooter(content) }) : null
  ] })
);
const Carousel = forwardRef(
  ({ children, hideNavigation = false, classNames, PreviousButton, NextButton, Pagination: Pagination2 }, ref) => {
    const numberOfItems = Children.count(children);
    return /* @__PURE__ */ jsxs("div", { className: cn("relative [--gap:1rem] [--slides-per-view:1]", classNames == null ? void 0 : classNames.wrapper), children: [
      PreviousButton && !hideNavigation && numberOfItems > 1 && PreviousButton(),
      /* @__PURE__ */ jsx(
        "div",
        {
          className: cn(`flex w-full snap-x snap-mandatory gap-[var(--gap)] overflow-x-scroll`, classNames == null ? void 0 : classNames.slider),
          ref,
          children: Children.map(children, (child, index2) => /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "min-w-0 flex-shrink-0 basis-[calc((100%_-_((var(--slides-per-view)_-_1)_*_var(--gap)))_/_var(--slides-per-view))] snap-start",
                classNames == null ? void 0 : classNames.item
              ),
              "data-index": index2,
              children: child
            }
          ))
        }
      ),
      NextButton && !hideNavigation && numberOfItems > 1 && NextButton(),
      Pagination2 && /* @__PURE__ */ jsx(Pagination2, { sliderRef: ref })
    ] });
  }
);
const Checkbox = forwardRef(
  ({
    rounded = false,
    disabled = false,
    checked,
    checkedIcon = /* @__PURE__ */ jsx(CheckIcon, {}),
    uncheckedIcon = null,
    indeterminateIcon = /* @__PURE__ */ jsx(IndeterminateIcon, {}),
    indeterminate,
    children,
    onChange,
    classNames,
    name,
    ...props
  }, ref) => /* @__PURE__ */ jsxs(
    "label",
    {
      ref,
      "aria-disabled": disabled,
      className: cn("relative isolate flex items-center p-1", classNames == null ? void 0 : classNames.wrapper),
      children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            name,
            value: "",
            disabled,
            "data-checked": checked,
            "data-indeterminate": indeterminate,
            checked,
            onChange,
            type: "checkbox",
            className: cn(
              "peer absolute left-0 top-0 m-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-default",
              classNames == null ? void 0 : classNames.input
            ),
            ...props
          }
        ),
        /* @__PURE__ */ jsx(
          "span",
          {
            className: cn(
              "h-em w-em flex-em relative block outline-none before:transition-[border]",
              "before:absolute before:left-0 before:top-0 before:h-full before:w-full before:border before:border-current",
              "peer-data-indeterminate:before:border-[0.5em] peer-checked:before:border-[0.5em]",
              "ring-offset-1 peer-focus-visible:ring peer-disabled:opacity-30",
              rounded ? "rounded-sm before:rounded-sm" : "rounded-none",
              classNames == null ? void 0 : classNames.checkbox
            ),
            children: /* @__PURE__ */ jsxs("div", { className: cn("pointer-events-none relative text-white", classNames == null ? void 0 : classNames.checkboxContainer), children: [
              checked ? checkedIcon : null,
              indeterminate ? indeterminateIcon : null,
              !checked ? uncheckedIcon : null
            ] })
          }
        ),
        /* @__PURE__ */ jsx("span", { className: cn("ml-2 text-base peer-disabled:opacity-30", classNames == null ? void 0 : classNames.label), children })
      ]
    }
  )
);
const Dialog = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  TitleComponent = "h2",
  DialogComponent = "div",
  SubtitleComponent,
  dialogTransitionProps,
  backdropTransitionProps,
  alignment = "center",
  CloseButton,
  classNames
}) => {
  const Description = subtitle && !SubtitleComponent ? "p" : SubtitleComponent;
  return /* @__PURE__ */ jsx(Transition, { appear: true, show: open, as: Fragment$1, children: /* @__PURE__ */ jsxs(
    Dialog$1,
    {
      as: DialogComponent,
      static: true,
      open,
      onClose,
      className: cn("relative z-50", classNames == null ? void 0 : classNames.container),
      children: [
        /* @__PURE__ */ jsx(
          Transition.Child,
          {
            as: Fragment$1,
            enter: "ease-out duration-300",
            enterFrom: "opacity-0",
            enterTo: "opacity-100",
            leave: "ease-in duration-200",
            leaveFrom: "opacity-100",
            leaveTo: "opacity-0",
            ...backdropTransitionProps,
            children: /* @__PURE__ */ jsx("div", { className: cn("fixed inset-0 bg-black/25", classNames == null ? void 0 : classNames.backdrop) })
          }
        ),
        /* @__PURE__ */ jsx(
          Transition.Child,
          {
            as: Fragment$1,
            enter: "ease-out duration-300",
            enterFrom: "opacity-0 translate-y-2",
            enterTo: "opacity-100 translate-y-0",
            leave: "ease-in duration-200",
            leaveFrom: "opacity-100 translate-y-0",
            leaveTo: "opacity-0 -translate-y-2",
            ...dialogTransitionProps,
            children: /* @__PURE__ */ jsx(
              "div",
              {
                className: cn(
                  "fixed inset-0 flex h-full justify-center",
                  alignment === "top" && "items-start",
                  alignment === "center" && "items-center",
                  alignment === "bottom" && "items-end"
                ),
                children: /* @__PURE__ */ jsxs(
                  Dialog$1.Panel,
                  {
                    className: cn("relative m-4 w-full max-w-md bg-white py-4 text-center", classNames == null ? void 0 : classNames.panel),
                    children: [
                      /* @__PURE__ */ jsxs("div", { className: cn("px-6", classNames == null ? void 0 : classNames.panelHeader), children: [
                        /* @__PURE__ */ jsx(Dialog$1.Title, { as: TitleComponent, className: classNames == null ? void 0 : classNames.title, children: title }),
                        subtitle || SubtitleComponent ? /* @__PURE__ */ jsx(Dialog$1.Description, { as: Description, className: classNames == null ? void 0 : classNames.subtitle, children: subtitle }) : null,
                        CloseButton && /* @__PURE__ */ jsx(CloseButton, {})
                      ] }),
                      children
                    ]
                  }
                )
              }
            )
          }
        )
      ]
    }
  ) });
};
const Divider = ({ vertical, className, style }) => /* @__PURE__ */ jsx(
  "hr",
  {
    style,
    className: cn("flex-none border-0 bg-gray-200", vertical ? "min-h-full w-0.5" : "h-0.5 min-w-full", className)
  }
);
const Drawer = ({ children, open = false }) => /* @__PURE__ */ jsx(
  Transition,
  {
    show: open,
    enter: "transition duration-75",
    enterFrom: "-translate-x-full",
    leave: "transition duration-75",
    enterTo: "translate-x-none",
    leaveFrom: "translate-x-none",
    leaveTo: "-translate-x-full",
    children: /* @__PURE__ */ jsx(
      "div",
      {
        id: "drawer-example",
        className: "fixed top-0 left-0 z-40 h-screen w-80 overflow-y-auto bg-gray-200 p-4",
        tabIndex: -1,
        "aria-labelledby": "drawer-label",
        children
      }
    )
  }
);
const Hero = ({
  children,
  className,
  WrapperComponent = "div"
}) => {
  const cols = Children.count(children);
  return /* @__PURE__ */ jsx(
    WrapperComponent,
    {
      className: cn(
        "relative grid grid-cols-1 md:max-h-[75vh] md:min-h-[50vh] xl:min-h-[75vh]",
        cols === 2 && "lg:grid-cols-2",
        cols >= 3 && "lg:grid-cols-3",
        className
      ),
      children
    }
  );
};
const inputStyle = cva("action-focus w-full border bg-transparent", {
  variants: {
    radius: {
      none: "rounded-none",
      rounded: "rounded-lg",
      full: "rounded-full"
    },
    dense: {
      true: "p-2",
      false: "p-4"
    },
    size: {
      md: "text-base",
      lg: "text-lg"
    }
  }
});
const Input$2 = forwardRef(
  ({
    name,
    startAdornment = null,
    endAdornment = null,
    fullWidth = false,
    inputSize = "md",
    type,
    dense = false,
    label = "",
    classNames,
    radius = "none",
    WrapperComponent = "div",
    hint,
    HintComponent = "div",
    hintId,
    ...props
  }, ref) => /* @__PURE__ */ jsxs(WrapperComponent, { className: cn("inline-block", fullWidth && "w-full", classNames == null ? void 0 : classNames.wrapper), children: [
    label && /* @__PURE__ */ jsxs("label", { className: cn("mb-1 block text-sm", classNames == null ? void 0 : classNames.label), htmlFor: name, children: [
      label,
      " ",
      props.required && "*"
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "relative w-full", children: [
      startAdornment,
      /* @__PURE__ */ jsx(
        "input",
        {
          ref,
          type,
          name,
          className: cn(
            inputStyle({ radius, dense, size: inputSize }),
            props.disabled && "opacity-50",
            classNames == null ? void 0 : classNames.input
          ),
          "aria-describedby": hintId,
          ...props
        }
      ),
      endAdornment
    ] }),
    hint && /* @__PURE__ */ jsx(HintComponent, { id: hintId, className: cn("mt-1 text-xs opacity-75", classNames == null ? void 0 : classNames.hint), children: hint })
  ] })
);
const DefaultLoader = /* @__PURE__ */ jsxs(
  "svg",
  {
    "aria-hidden": "true",
    className: "inline h-8 w-8 animate-spin fill-black text-black/10",
    viewBox: "0 0 100 101",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z",
          fill: "currentFill"
        }
      )
    ]
  }
);
function Loader({ WrapperComponent = "div", LoaderComponent = DefaultLoader }) {
  return /* @__PURE__ */ jsxs(WrapperComponent, { role: "status", className: "flex justify-center", children: [
    LoaderComponent,
    /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Loading" })
  ] });
}
const defaultRenderItem$1 = (option, { active, disabled }) => /* @__PURE__ */ jsx(
  "span",
  {
    className: `${active ? "bg-white" : "bg-transparent"} ${disabled ? "opacity-30" : "cursor-pointer"} block py-2 px-4`,
    children: option.label ?? option
  }
);
const Menu = ({
  options,
  children,
  alignment = "start",
  allowedPlacements = [`top-${alignment}`, `bottom-${alignment}`],
  renderItem = defaultRenderItem$1,
  getOptionId = (i) => i.id ?? i,
  getOptionDisabled = (i) => i.disabled,
  ListComponent = "ul",
  ButtonComponent = "button",
  OptionComponent = "li",
  transitionProps,
  classNames,
  offset: defaultOffset = 4,
  sameWidth = false
}) => {
  const {
    reference,
    floating,
    strategy: position,
    x,
    y
  } = useFloating({
    middleware: [
      autoPlacement({
        alignment,
        allowedPlacements
      }),
      offset(defaultOffset),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            ...sameWidth && { width: `${rects.reference.width}px` }
          });
        }
      })
    ]
  });
  return /* @__PURE__ */ jsx(Menu$1, { as: "div", className: classNames == null ? void 0 : classNames.container, children: ({ open }) => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { ref: reference, children: /* @__PURE__ */ jsx(Menu$1.Button, { as: ButtonComponent, className: cn("rounded py-2 px-4", classNames == null ? void 0 : classNames.button), children }) }),
    open && (options == null ? void 0 : options.length) ? /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(
      Transition,
      {
        as: Fragment$1,
        appear: true,
        show: open,
        enter: "transition ease-out duration-300",
        enterFrom: "opacity-0 -translate-y-2",
        enterTo: "opacity-100 translate-y-0",
        ...transitionProps,
        children: /* @__PURE__ */ jsx(
          "div",
          {
            ref: floating,
            style: { position, left: Number(x) ?? 0, top: Number(y) ?? 0 },
            className: classNames == null ? void 0 : classNames.floatingMenu,
            children: /* @__PURE__ */ jsx(
              Menu$1.Items,
              {
                as: ListComponent,
                className: cn("flex flex-col items-stretch rounded py-1", classNames == null ? void 0 : classNames.listWrapper),
                children: options.map((option) => /* @__PURE__ */ jsx(
                  Menu$1.Item,
                  {
                    as: OptionComponent,
                    disabled: getOptionDisabled(option),
                    children: ({ disabled, active }) => renderItem(option, { disabled, active })
                  },
                  getOptionId(option)
                ))
              }
            )
          }
        )
      }
    ) }) : null
  ] }) });
};
const Button = ({ className, ...props }) => /* @__PURE__ */ jsx(
  "button",
  {
    ...props,
    type: "button",
    className: cn(
      "flex h-8 w-8 items-center justify-center rounded text-base hover:bg-gray-100 disabled:bg-transparent disabled:opacity-30",
      className
    )
  }
);
const Pagination = ({
  page,
  setPage,
  totalPages = 0,
  pageRange = 3,
  InputComponent = "input",
  ButtonComponent = Button,
  PaginationComponent = "div",
  getPage = (v) => v,
  getFirst = () => "<<",
  getPrev = () => "<",
  getNext = () => ">",
  getLast = () => ">>",
  classNames,
  showEndPages = false
}) => {
  const inputRef = useRef(null);
  const handleSubmit = (e) => {
    var _a;
    e.preventDefault();
    let pageNumber = Number((_a = inputRef == null ? void 0 : inputRef.current) == null ? void 0 : _a.value) - 1;
    if (pageNumber >= totalPages)
      pageNumber = totalPages - 1;
    if (pageNumber <= 0)
      pageNumber = 0;
    setPage(pageNumber);
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.blur();
    }
  };
  const pages = useMemo(() => {
    if (pageRange === 0)
      return Array.from({ length: totalPages }, (_, i) => i);
    const start = Math.max(0, page - pageRange);
    const end = Math.min(totalPages, page + pageRange + 1);
    return Array.from({ length: end - start }, (_, i) => i + start);
  }, [page, totalPages, pageRange]);
  return /* @__PURE__ */ jsx("nav", { "aria-label": "Pagination", children: /* @__PURE__ */ jsxs(PaginationComponent, { className: "flex", children: [
    /* @__PURE__ */ jsx(ButtonComponent, { disabled: page === 0, onClick: () => setPage(0), className: classNames == null ? void 0 : classNames.endButtons, children: getFirst(page) }),
    /* @__PURE__ */ jsx(
      ButtonComponent,
      {
        disabled: page === 0,
        onClick: () => setPage(Math.max(0, page - 1)),
        className: classNames == null ? void 0 : classNames.stepButtons,
        children: getPrev(page)
      }
    ),
    /* @__PURE__ */ jsxs("ul", { className: cn("flex", classNames == null ? void 0 : classNames.pagesContainer), children: [
      showEndPages && // eslint-disable-next-line react/jsx-no-useless-fragment
      /* @__PURE__ */ jsx(Fragment, { children: page > pageRange && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
          ButtonComponent,
          {
            onClick: () => setPage(0),
            className: classNames == null ? void 0 : classNames.pageButton,
            "aria-label": "Goto page 1",
            children: "1"
          }
        ) }),
        page > pageRange + 1 && /* @__PURE__ */ jsx("li", { "aria-hidden": true, className: cn("h-8 w-8 text-center leading-[2rem]", classNames == null ? void 0 : classNames.ellipsis), children: "..." })
      ] }) }),
      pages.map((currentPage) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
        ButtonComponent,
        {
          onClick: () => setPage(currentPage),
          "aria-current": page === currentPage,
          "data-headlessui-state": page === currentPage ? "active" : "",
          "aria-label": `Goto page ${currentPage + 1}`,
          className: cn("ui-active:bg-blue-500 ui-active:text-white", classNames == null ? void 0 : classNames.pageButton),
          children: getPage(currentPage + 1)
        }
      ) }, currentPage)),
      showEndPages && // eslint-disable-next-line react/jsx-no-useless-fragment
      /* @__PURE__ */ jsx(Fragment, { children: page + 1 < totalPages - pageRange && /* @__PURE__ */ jsxs(Fragment, { children: [
        page + 1 < totalPages - pageRange - 1 && /* @__PURE__ */ jsx("li", { "aria-hidden": true, className: cn("h-8 w-8 text-center leading-[2rem]", classNames == null ? void 0 : classNames.ellipsis), children: "..." }),
        /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
          ButtonComponent,
          {
            onClick: () => setPage(totalPages - 1),
            className: classNames == null ? void 0 : classNames.pageButton,
            "aria-label": `Goto page ${totalPages - 1}`,
            children: totalPages
          }
        ) })
      ] }) })
    ] }),
    InputComponent ? /* @__PURE__ */ jsx("form", { onSubmit: handleSubmit, children: /* @__PURE__ */ jsx(
      InputComponent,
      {
        className: "h-8 w-8 appearance-none rounded text-center",
        ref: inputRef,
        type: "number",
        placeholder: String(page + 1),
        min: 1,
        max: pages
      }
    ) }) : null,
    /* @__PURE__ */ jsx(
      ButtonComponent,
      {
        disabled: page === totalPages - 1,
        onClick: () => setPage(Math.min(page + 1, totalPages - 1)),
        className: classNames == null ? void 0 : classNames.stepButtons,
        children: getNext(page)
      }
    ),
    /* @__PURE__ */ jsx(
      ButtonComponent,
      {
        disabled: page === totalPages - 1,
        onClick: () => setPage(totalPages - 1),
        className: classNames == null ? void 0 : classNames.endButtons,
        children: getLast(page)
      }
    )
  ] }) });
};
const Quantity = ({
  value,
  dense = false,
  disabled = false,
  onDecrease,
  onIncrease,
  onBlur,
  onChange,
  DecreaseIcon = /* @__PURE__ */ jsx(MinusIcon, { className: "mx-2 h-4 w-4" }),
  IncreaseIcon = /* @__PURE__ */ jsx(PlusIcon, { className: "mx-2 h-4 w-4" }),
  classNames,
  ...rest
}) => /* @__PURE__ */ jsxs(
  "div",
  {
    className: cn(
      "color-black flex border border-black bg-white",
      dense ? "h-8" : "h-12",
      disabled ? "pointer-events-none opacity-50" : "",
      classNames == null ? void 0 : classNames.wrapper
    ),
    children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          "aria-label": "decreaseButton",
          type: "button",
          className: cn("flex-shrink-0 basis-7 bg-white transition hover:opacity-75", classNames == null ? void 0 : classNames.decreaseButton),
          onClick: onDecrease,
          children: DecreaseIcon
        }
      ),
      /* @__PURE__ */ jsx(
        "input",
        {
          ...rest,
          type: "number",
          className: cn("w-full text-center text-sm", classNames == null ? void 0 : classNames.input),
          value: value || "",
          onChange,
          onBlur
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: cn("flex-shrink-0 basis-7 bg-white transition hover:opacity-75", classNames == null ? void 0 : classNames.increaseButton),
          "aria-label": "increaseButton",
          onClick: onIncrease,
          children: IncreaseIcon
        }
      )
    ]
  }
);
const Radio = forwardRef(
  ({
    id = "checkbox-id",
    disabled,
    checked,
    indeterminate,
    children,
    style,
    onChange,
    classNames,
    ...props
  }, ref) => /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
    /* @__PURE__ */ jsx("div", { className: "relative mr-2 h-5 w-5 text-sm", children: /* @__PURE__ */ jsx(
      "input",
      {
        checked,
        id,
        type: "radio",
        value: "",
        disabled,
        onChange,
        className: cn(
          "action-focus grid h-full w-full appearance-none rounded-full border border-current text-current",
          "before:block before:h-full before:w-full before:scale-0 before:rounded-[inherit] before:bg-current",
          "checked:before:scale-75",
          indeterminate && "before:scale-75",
          classNames == null ? void 0 : classNames.checkbox
        ),
        ...props
      }
    ) }),
    /* @__PURE__ */ jsx("label", { ref, style, htmlFor: id, className: cn("cursor-pointer text-base", classNames == null ? void 0 : classNames.label), children })
  ] })
);
function useOnClickOutside(ref, handler, state) {
  useEffect(() => {
    const listener = (event) => {
      var _a, _b;
      if (!((_a = ref.current) == null ? void 0 : _a.contains(event.target)))
        handler(event);
      else if (event.target !== ((_b = state.inputRef) == null ? void 0 : _b.current))
        event.preventDefault();
    };
    if (state.open) {
      document.addEventListener("mousedown", listener);
      document.addEventListener("touchstart", listener);
    }
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler, state]);
}
const Context = createContext({
  open: false,
  selected: null,
  observer: void 0,
  inputRef: void 0,
  wrapperRef: void 0,
  onBlur: (e) => Promise.resolve(e),
  onFocus: (e) => Promise.resolve(e),
  onClick: () => null,
  onKeyDown: () => null,
  setSelected: () => null
});
const Provider = ({ onChange = () => null, onSearch, wrapperRef, children }) => {
  const [selected, setSelected] = useState(null);
  const callback = (nodes) => nodes.forEach(
    (node) => !node.isIntersecting && node.target.scrollIntoView({
      block: "nearest"
    })
  );
  const [observer, setObserver] = useState();
  useEffect(() => {
    setObserver(
      new IntersectionObserver(callback, {
        root: wrapperRef == null ? void 0 : wrapperRef.current,
        rootMargin: "40px 0px 0px 0px",
        threshold: 1
      })
    );
  }, [wrapperRef]);
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(false);
  const inputRef = useRef(null);
  const onBlur = useCallback(
    (event) => new Promise((resolve) => {
      setFocus(false);
      resolve(event);
    }),
    []
  );
  const onFocus = useCallback(
    (event) => new Promise((resolve) => {
      setFocus(true);
      resolve(event);
    }),
    []
  );
  useEffect(() => {
    if (focus) {
      setOpen(true);
    } else {
      setOpen(false);
      setSelected(null);
    }
  }, [focus, setSelected]);
  const onKeyDown = useCallback(
    (event) => {
      if (!(wrapperRef == null ? void 0 : wrapperRef.current))
        return;
      const nodes = wrapperRef == null ? void 0 : wrapperRef.current.querySelectorAll('*[role="option"]:not([aria-disabled="true"])');
      const data = Array.from(nodes).map((el) => el.getAttribute("value"));
      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          event.stopPropagation();
          const current = data.indexOf(selected);
          setSelected(data[Math.min(data.length - 1, current + 1)]);
          break;
        }
        case "PageDown": {
          event.preventDefault();
          event.stopPropagation();
          const current = data.indexOf(selected);
          setSelected(data[Math.min(data.length - 1, current + 5)]);
          break;
        }
        case "End":
          event.preventDefault();
          event.stopPropagation();
          setSelected(data[data.length - 1]);
          break;
        case "ArrowUp": {
          event.preventDefault();
          event.stopPropagation();
          const current = data.indexOf(selected);
          if (current > 0)
            setSelected(data[Math.max(-1, current - 1)]);
          else
            setSelected(null);
          break;
        }
        case "PageUp": {
          event.preventDefault();
          event.stopPropagation();
          const current = data.indexOf(selected);
          setSelected(data[Math.max(0, current - 5)]);
          break;
        }
        case "Home":
          event.preventDefault();
          event.stopPropagation();
          setSelected(data[0]);
          break;
        case "Escape":
          event.preventDefault();
          event.stopPropagation();
          event.target.blur();
          break;
        case "Enter": {
          event.preventDefault();
          event.stopPropagation();
          const input = event.target;
          if (selected && data.includes(selected))
            onSearch(selected);
          else {
            setSelected(null);
            onSearch(input.value);
          }
          input.blur();
          break;
        }
      }
    },
    [selected, wrapperRef, onSearch]
  );
  useEffect(() => {
    if (onChange)
      onChange(selected);
  }, [onChange, selected]);
  const onClick = useCallback(
    (event) => {
      var _a;
      const value2 = event.currentTarget.getAttribute("value");
      if (value2)
        onSearch(value2);
      (_a = document.activeElement) == null ? void 0 : _a.blur();
    },
    [onSearch]
  );
  const value = useMemo(
    () => ({
      open,
      observer,
      inputRef,
      wrapperRef,
      selected,
      setSelected,
      onBlur,
      onFocus,
      onClick,
      onKeyDown
    }),
    [open, observer, inputRef, wrapperRef, selected, setSelected, onBlur, onFocus, onClick, onKeyDown]
  );
  useOnClickOutside(wrapperRef, () => setOpen(false), value);
  return /* @__PURE__ */ jsx(Context.Provider, { value, children });
};
const useSearch = () => {
  const context = useContext(Context);
  if (context === void 0)
    throw new Error("No context found");
  return context;
};
const List = ({ children, className, ...rest }) => {
  const { open, selected } = useSearch();
  if (!open)
    return null;
  return /* @__PURE__ */ jsx(
    "ul",
    {
      "data-active": selected,
      className: cn("absolute left-0 top-full z-50 flex min-w-full flex-col bg-inherit shadow", className),
      ...rest,
      children
    }
  );
};
const Input = forwardRef(
  ({
    placeholder,
    className,
    children = /* @__PURE__ */ jsx(SearchIcon, { className: "mr-2" }),
    ...rest
  }, ref) => /* @__PURE__ */ jsxs("div", { className: cn("flex items-center p-2", className), children: [
    children,
    /* @__PURE__ */ jsx("input", { ...rest, className: "w-full focus:outline-none", ref, placeholder })
  ] })
);
const Input$1 = ({
  className,
  onBlur: blurHandler = () => null,
  onFocus: focusHandler = () => null,
  value,
  children,
  ...props
}) => {
  const { selected, onBlur, onFocus, onKeyDown, inputRef } = useSearch();
  const handleBlur = (event) => onBlur(event).then(blurHandler);
  const handleFocus = (event) => onFocus(event).then(focusHandler);
  return /* @__PURE__ */ jsx(
    Input,
    {
      ...props,
      ref: inputRef,
      value,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onKeyDown,
      className,
      "data-active-option": selected,
      children
    }
  );
};
function eventToPosition(evt) {
  return [evt.screenX, evt.screenY];
}
function useTrackedPointer() {
  const lastPos = useRef([-1, -1]);
  return {
    wasMoved(evt) {
      const newPos = eventToPosition(evt);
      if (lastPos.current[0] === newPos[0] && lastPos.current[1] === newPos[1])
        return false;
      lastPos.current = newPos;
      return true;
    },
    update(evt) {
      lastPos.current = eventToPosition(evt);
    }
  };
}
const Suggestion = ({ value, disabled, children, className, ...rest }) => {
  const ref = useRef(null);
  const { observer, selected, setSelected, onClick } = useSearch();
  const pointer = useTrackedPointer();
  const handleEnter = (event) => {
    pointer.update(event);
    if (value)
      setSelected(value);
  };
  const handleMove = (event) => {
    if (!pointer.wasMoved(event))
      return;
    if (disabled)
      return;
    setSelected(value);
  };
  const handleLeave = (event) => {
    if (!pointer.wasMoved(event))
      return;
    if (disabled)
      return;
    setSelected(null);
  };
  useEffect(() => {
    var _a;
    if (selected === value && (ref == null ? void 0 : ref.current))
      (_a = observer == null ? void 0 : observer.observe) == null ? void 0 : _a.call(observer, ref.current);
    return () => {
      var _a2;
      return (_a2 = observer == null ? void 0 : observer.disconnect) == null ? void 0 : _a2.call(observer);
    };
  }, [value, selected, observer]);
  return /* @__PURE__ */ jsx(
    "li",
    {
      onClick,
      onMouseMove: handleMove,
      onMouseEnter: handleEnter,
      onMouseLeave: handleLeave,
      ...rest,
      ref,
      role: "option",
      value,
      tabIndex: -1,
      "aria-disabled": disabled,
      "aria-selected": selected === value,
      className: cn(
        "cursor-pointer select-none px-8 py-2 aria-disabled:pointer-events-none aria-disabled:opacity-30 aria-selected:bg-gray-100",
        className
      ),
      children
    }
  );
};
const Search = ({ children, className, onChange = () => null, onSearch = () => null, ...rest }) => {
  const ref = useRef(null);
  return /* @__PURE__ */ jsx(Provider, { wrapperRef: ref, onSearch, onChange, children: /* @__PURE__ */ jsx("div", { className: cn("group relative bg-white", className), ref, ...rest, children }) });
};
Search.List = List;
Search.Input = Input$1;
Search.Suggestion = Suggestion;
const defaultRenderItem = (option, { active, selected, disabled, getOptionLabel }) => {
  const isActive = active ?? selected;
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: `
      block py-2 px-4
      ${isActive ? "bg-white" : "bg-transparent"}
      ${disabled ? "opacity-30" : "cursor-pointer"}
    `,
      children: getOptionLabel == null ? void 0 : getOptionLabel(option)
    }
  );
};
const defaultRenderValue = (options, { getOptionLabel }) => (!Array.isArray(options) ? [options] : options).map((option) => getOptionLabel == null ? void 0 : getOptionLabel(option)).join(", ");
const Select = ({
  label,
  value,
  options,
  disabled,
  onChange,
  alignment = "start",
  placeholder = "No selection...",
  getOptionId = (o) => o.id ?? o,
  getOptionValue = (o) => o,
  getOptionLabel = getOptionValue,
  getOptionDisabled = (o) => o.disabled,
  renderItem = defaultRenderItem,
  renderValue = defaultRenderValue,
  ListComponent = "ul",
  ButtonComponent = "button",
  OptionComponent = "li",
  transitionProps
}) => {
  const {
    reference,
    floating,
    strategy: position,
    x,
    y
  } = useFloating({
    middleware: [
      autoPlacement({
        alignment,
        allowedPlacements: [`top-${alignment}`, `bottom-${alignment}`]
      }),
      offset(4)
    ]
  });
  const isEmpty = Array.isArray(value) ? !value.length : !value;
  return /* @__PURE__ */ jsx(
    Listbox,
    {
      disabled,
      value,
      onChange,
      by: (a, b) => a && b && getOptionValue(a) === getOptionValue(b),
      multiple: Array.isArray(value),
      children: ({ open }) => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Listbox.Label, { children: label }),
        /* @__PURE__ */ jsx("div", { ref: reference, children: /* @__PURE__ */ jsx(
          Listbox.Button,
          {
            as: ButtonComponent,
            className: `bg-paper flex flex-row rounded py-2 px-4 ${isEmpty && "text-gray-500"}`,
            children: isEmpty ? placeholder : renderValue(value, { getOptionValue, getOptionLabel })
          }
        ) }),
        open ? /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(
          Transition,
          {
            as: Fragment$1,
            appear: true,
            show: open,
            enter: "ease-out duration-300",
            enterFrom: "opacity-0 -translate-y-2",
            enterTo: "opacity-100 translate-y-0",
            ...transitionProps,
            children: /* @__PURE__ */ jsx("div", { ref: floating, style: { position, left: Number(x), top: Number(y) }, children: /* @__PURE__ */ jsx(Listbox.Options, { as: ListComponent, className: "bg-paper flex flex-col items-stretch rounded py-1", children: options.map((option) => /* @__PURE__ */ jsx(
              Listbox.Option,
              {
                as: OptionComponent,
                value: option,
                disabled: getOptionDisabled(option),
                children: ({ disabled: d, active, selected }) => renderItem(option, { disabled: d, active, selected, getOptionValue, getOptionLabel })
              },
              getOptionId(option)
            )) }) })
          }
        ) }) : null
      ] })
    }
  );
};
const updateValueIndex = (values, index2, value) => {
  const ret = values.slice(0);
  ret[index2] = value;
  return ret.sort((a, b) => a - b);
};
function getClosestIndex(track, vertical, { clientX, clientY }) {
  let thumbIndex = 0;
  let thumbDistance = Infinity;
  const thumbs = Array.from(((track == null ? void 0 : track.current) ?? document).querySelectorAll('[role="slider"'));
  for (let i = 0; i < thumbs.length; i += 1) {
    const { left, top, width, height } = thumbs[i].getBoundingClientRect();
    const distance = Math.abs(vertical ? clientY - (top + height / 2) : clientX - (left + width / 2));
    if (distance < thumbDistance) {
      thumbDistance = distance;
      thumbIndex = i;
    }
  }
  thumbs[thumbIndex].focus();
  return thumbIndex;
}
const getTargetIndex = (target, track) => {
  var _a, _b;
  const slider = target.closest('[role="slider"]');
  if (!slider)
    return -1;
  const index2 = Array.from(((_b = (_a = track.current) == null ? void 0 : _a.querySelectorAll) == null ? void 0 : _b.call(_a, '[role="slider"]')) ?? []).indexOf(slider);
  return index2;
};
const getStepDecimals = (step) => {
  const decimals = step.toString().split(".")[1];
  return decimals ? decimals.length : 0;
};
const getClosestTick = (value, ticks) => {
  let closestTick = 0;
  let closestValue = Infinity;
  for (let i = 0; i < ticks.length; i += 1) {
    const distance = Math.abs(ticks[i] - value);
    if (distance < closestValue) {
      closestValue = distance;
      closestTick = ticks[i];
    }
  }
  return closestTick;
};
const normalizeValue = (rawValue, index2, min, max, step, values, ticks = [], latchToTick = false, allowOverlap = true) => {
  const BIG_NUM = 1e11;
  const value = Math.round(rawValue * BIG_NUM) / BIG_NUM;
  if (!allowOverlap) {
    const prev = values[index2 - 1];
    const next = values[index2 + 1];
    if (prev && prev > value)
      return prev;
    if (next && next < value)
      return next;
  }
  if (value > max)
    return max;
  if (value < min)
    return min;
  const remainder = Math.floor(value * BIG_NUM - min * BIG_NUM) % Math.floor(step * BIG_NUM);
  const closestLowerNum = Math.floor(value * BIG_NUM - Math.abs(remainder));
  const rounded = remainder === 0 ? value : closestLowerNum / BIG_NUM;
  const res = Math.abs(remainder / BIG_NUM) < step / 2 ? rounded : rounded + step;
  const decimalPlaces = getStepDecimals(step);
  const normalizedValue = parseFloat(res.toFixed(decimalPlaces));
  if (latchToTick)
    return getClosestTick(normalizedValue, ticks);
  return normalizedValue;
};
const getTrackProgress = ({
  value,
  min,
  max,
  vertical = false
}) => {
  const dir = vertical ? "to bottom" : "to right";
  const valuesInPercent = (Array.isArray(value) ? value : [value]).map((val) => (val - min) / (max - min) * 100);
  const [start, end] = [valuesInPercent.shift(), valuesInPercent.pop()];
  if (!end) {
    return `linear-gradient(${dir}, currentColor 0%, currentColor ${start}%, transparent ${start}%, transparent 100%)`;
  }
  return `linear-gradient(${dir}, transparent 0%, transparent ${start}%, currentColor ${start}%, currentColor ${end}%, transparent ${end}%, transparent 100%)`;
};
const getThumbOffset = ({
  value,
  min,
  max,
  vertical = false
}) => {
  const valueInPercent = `${(value - min) / (max - min) * 100}%`;
  return vertical ? { top: valueInPercent } : { left: valueInPercent };
};
const INCREASE_KEYS = ["ArrowRight", "ArrowUp", "k", "PageUp"];
const DECREASE_KEYS = ["ArrowLeft", "ArrowDown", "j", "PageDown"];
const useSlider = ({
  value,
  onChange,
  onRelease,
  min = 0,
  max = 100,
  step = 1,
  ticks = [],
  vertical = false,
  latchToTick,
  allowOverlap
}) => {
  const valueRef = useRef([]);
  const indexRef = useRef(-1);
  const [activeThumb, setActiveThumb] = useState(-1);
  const values = useMemo(() => Array.isArray(value) ? value : [value], [value]);
  const track = useRef(null);
  const [thumbs, setThumbs] = useState([]);
  useEffect(() => {
    const valueArray = Array.isArray(values) ? values : [values];
    if (valueArray.length !== thumbs.length)
      setThumbs(valueArray.map(() => createRef()));
  }, [thumbs, values]);
  const marks = useMemo(() => {
    const range = max - min;
    const ticksMinMax = Array.from(/* @__PURE__ */ new Set([min, ...ticks, max]));
    return ticksMinMax.map((i) => ({ value: i, position: `${(i - min) / range * 100}%` }));
  }, [min, max, ticks]);
  const onKeyDown = useCallback(
    (event) => {
      var _a, _b, _c;
      let newValue = [...values][indexRef.current];
      if (INCREASE_KEYS.includes(event.key)) {
        if (latchToTick) {
          newValue = ticks[Math.min(ticks.length - 1, ticks.indexOf(newValue) + 1)];
        } else {
          newValue += step;
        }
      }
      if (DECREASE_KEYS.includes(event.key)) {
        if (latchToTick) {
          newValue = ticks[Math.max(0, ticks.indexOf(newValue) - 1)];
        } else {
          newValue -= step;
        }
      }
      const normalizedValue = normalizeValue(newValue, indexRef.current, min, max, step, values);
      const newValues = updateValueIndex(values, indexRef.current, normalizedValue);
      indexRef.current = newValues.indexOf(normalizedValue);
      (_c = (_b = (_a = thumbs == null ? void 0 : thumbs[indexRef.current]) == null ? void 0 : _a.current) == null ? void 0 : _b.focus) == null ? void 0 : _c.call(_b);
      onChange(newValues);
    },
    [min, max, step, values, thumbs, onChange, ticks, latchToTick]
  );
  const onKeyUp = useCallback(
    (event) => {
      const index2 = getTargetIndex(event.currentTarget, track);
      onRelease == null ? void 0 : onRelease(
        updateValueIndex(
          values,
          index2,
          normalizeValue(values[index2], index2, min, max, step, values, ticks, latchToTick, allowOverlap)
        )
      );
    },
    [min, max, step, values, onRelease, ticks, latchToTick, allowOverlap]
  );
  const onMove = useCallback(
    (event, commit = false) => {
      var _a, _b, _c, _d, _e, _f;
      if (!track.current)
        return;
      let { clientX, clientY } = event;
      if ((_a = event.changedTouches) == null ? void 0 : _a.length)
        ({ clientX, clientY } = event.changedTouches.item(0));
      if ((_b = event.touches) == null ? void 0 : _b.length)
        ({ clientX, clientY } = event.touches.item(0));
      const { top, left, width, height } = track.current.getBoundingClientRect();
      let newValue = (clientX - left) / width * (max - min) + min;
      if (vertical)
        newValue = (clientY - top) / height * (max - min) + min;
      const normalizedValue = normalizeValue(
        newValue,
        indexRef.current,
        min,
        max,
        step,
        valueRef.current,
        ticks,
        latchToTick,
        allowOverlap
      );
      valueRef.current = updateValueIndex(valueRef.current, indexRef.current, normalizedValue);
      if (commit) {
        onRelease == null ? void 0 : onRelease(valueRef.current);
        setActiveThumb(-1);
        (_c = document.activeElement) == null ? void 0 : _c.blur();
      } else {
        onChange(valueRef.current);
        indexRef.current = valueRef.current.indexOf(normalizedValue);
        setActiveThumb(indexRef.current);
        (_f = (_e = (_d = thumbs == null ? void 0 : thumbs[indexRef.current]) == null ? void 0 : _d.current) == null ? void 0 : _e.focus) == null ? void 0 : _f.call(_e);
      }
    },
    [onChange, onRelease, vertical, min, max, step, thumbs, ticks, latchToTick, allowOverlap]
  );
  const onEnd = useCallback(
    (event) => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchmove", onMove, { passive: true });
      document.removeEventListener("touchend", onEnd, { passive: true });
      document.removeEventListener("touchcancel", onEnd, { passive: true });
      onMove(event, true);
      document.body.style.cursor = "";
      indexRef.current = -1;
      valueRef.current = [];
    },
    [onMove]
  );
  const onStart = useCallback(
    (event) => {
      valueRef.current = [...values];
      event.stopPropagation();
      if (indexRef.current < 0)
        indexRef.current = getTargetIndex(event.target, track);
      setActiveThumb(indexRef.current);
      if (indexRef.current === -1)
        return;
      document.body.style.cursor = "grabbing";
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onEnd);
      document.addEventListener("touchmove", onMove, { passive: true });
      document.addEventListener("touchend", onEnd, { passive: true });
      document.addEventListener("touchcancel", onEnd, { passive: true });
    },
    [onEnd, onMove, values]
  );
  const onSearch = (e) => {
    indexRef.current = getClosestIndex(track, vertical, e);
    onStart(e);
    onMove(e);
  };
  return { marks, track, thumbs, onSearch, onKeyDown, onKeyUp, onStart, activeThumb, setActiveThumb, indexRef };
};
const Slider = ({
  min = 0,
  max = 100,
  step = 1,
  ticks = [],
  value,
  vertical = false,
  onChange: handleChange,
  onRelease,
  className,
  latchToTick = false,
  allowOverlap = true,
  RailComponent = "div",
  MarkComponent = "div",
  ThumbComponent = "div",
  LabelComponent = "span"
}) => {
  const onChange = (v) => handleChange(v.length === 1 ? v[0] : v);
  const { marks, track, thumbs, onSearch, onKeyDown, onKeyUp, onStart, indexRef } = useSlider({
    min,
    max,
    step,
    ticks,
    value,
    vertical,
    onChange,
    onRelease,
    latchToTick,
    allowOverlap
  });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: track,
      onMouseDown: onSearch,
      className: ["relative cursor-pointer", vertical ? "h-full w-fit px-2" : "h-fit w-full py-2"].join(" "),
      children: [
        /* @__PURE__ */ jsx(
          RailComponent,
          {
            className: [
              "relative flex justify-center rounded bg-neutral-100",
              vertical ? "h-full w-1" : "h-1 w-full",
              className
            ].join(" "),
            children: /* @__PURE__ */ jsx(
              "div",
              {
                className: "absolute h-full w-full rounded-[inherit] text-gray-800",
                style: { background: getTrackProgress({ value, min, max, vertical }) }
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: [
              "absolute select-none",
              vertical ? "left-full top-0 h-full w-fit" : "top-full left-0 h-fit w-full"
            ].join(" "),
            children: marks.map(({ value: v, position: left }) => /* @__PURE__ */ jsx(
              MarkComponent,
              {
                className: [
                  "absolute transform text-xs tabular-nums",
                  vertical ? "-translate-y-1/2" : "-translate-x-1/2"
                ].join(" "),
                style: { ...vertical ? { top: left } : { left } },
                children: v
              },
              v
            ))
          }
        ),
        (!Array.isArray(value) ? [value] : value).map((val, index2) => /* @__PURE__ */ jsxs(
          ThumbComponent,
          {
            ref: thumbs[index2],
            role: "slider",
            "aria-valuemin": min,
            "aria-valuemax": max,
            "aria-valuenow": val,
            "aria-label": "slider-handle",
            onMouseDown: onStart,
            onTouchStart: onStart,
            onKeyDown,
            onKeyUp,
            tabIndex: 0,
            onFocus: () => {
              indexRef.current = index2;
            },
            onBlur: ({ relatedTarget }) => {
              if (relatedTarget)
                indexRef.current = -1;
            },
            className: "group absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform cursor-grab p-2 transition duration-150 focus-visible:outline-none",
            style: { zIndex: index2, ...getThumbOffset({ value: val, min, max, vertical }) },
            draggable: false,
            children: [
              /* @__PURE__ */ jsx("div", { className: "h-3 w-3 rounded bg-neutral-500 group-focus:ring" }),
              /* @__PURE__ */ jsx(
                LabelComponent,
                {
                  className: [
                    "absolute rounded bg-neutral-900 p-1 text-xs tabular-nums text-white opacity-0 transition group-hover:opacity-100 group-focus:opacity-100",
                    vertical ? "left-full top-1/2 -translate-y-1/2 translate-x-0 group-hover:translate-x-1 group-focus:translate-x-1" : "bottom-full left-1/2 -translate-x-1/2 translate-y-0 group-hover:-translate-y-1 group-focus:-translate-y-1"
                  ].join(" "),
                  children: val
                }
              )
            ]
          },
          index2
        ))
      ]
    }
  );
};
const Splash = ({
  children,
  title,
  titleSize = "text-lg",
  description,
  classNames,
  LinkComponent,
  ImageComponent,
  TitleComponent = "h2",
  SubtitleComponent = "p"
}) => /* @__PURE__ */ jsxs("div", { className: cn("relative aspect-square", classNames == null ? void 0 : classNames.wrapper), children: [
  ImageComponent ? /* @__PURE__ */ jsx(ImageComponent, { className: "h-full w-full object-cover" }) : null,
  /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "absolute inset-0 flex flex-col items-center justify-center p-4 text-center text-white",
        classNames == null ? void 0 : classNames.container
      ),
      children: [
        /* @__PURE__ */ jsx(TitleComponent, { className: cn(titleSize, "mb-4 font-bold uppercase", classNames == null ? void 0 : classNames.title), children: title }),
        description ? /* @__PURE__ */ jsx(SubtitleComponent, { className: cn("mx-auto max-w-lg text-lg", classNames == null ? void 0 : classNames.description), children: description }) : null,
        children
      ]
    }
  ),
  LinkComponent ? /* @__PURE__ */ jsx(LinkComponent, { className: "absolute inset-0" }) : null
] });
const Tab = ({ children }) => children;
const Tabs = ({
  onChange,
  vertical = false,
  defaultIndex,
  selectedIndex,
  TabsComponent = "div",
  TabComponent = "button",
  ContentComponent = "div",
  children
}) => {
  const validTabs = Children.toArray(children).filter(
    (child) => isValidElement(child) && child.type === Tab && child.props.title
  );
  return /* @__PURE__ */ jsxs(
    Tab$1.Group,
    {
      vertical,
      onChange,
      defaultIndex,
      selectedIndex,
      children: [
        /* @__PURE__ */ jsx(Tab$1.List, { className: "flex flex-nowrap overflow-auto rounded bg-gray-100 p-1", as: TabsComponent, children: validTabs.map((child) => /* @__PURE__ */ jsx(
          Tab$1,
          {
            className: "ui-selected:bg-gray-800 ui-selected:text-white flex-none rounded py-2 px-4 disabled:opacity-30",
            as: TabComponent,
            ...{ disabled: child.props.disabled },
            children: child.props.title
          },
          child.key
        )) }),
        /* @__PURE__ */ jsx(Tab$1.Panels, { as: Fragment$1, children: validTabs.map((child) => /* @__PURE__ */ jsx(Tab$1.Panel, { as: ContentComponent, children: child }, child.key)) })
      ]
    }
  );
};
const textareaStyle = cva("action-focus w-full border bg-transparent align-top", {
  variants: {
    radius: {
      none: "rounded-none",
      rounded: "rounded-lg"
    },
    dense: {
      true: "p-2",
      false: "p-4"
    },
    size: {
      md: "text-base",
      lg: "text-lg"
    },
    resizable: {
      none: "resize-none",
      horizontal: "resize-x",
      vertical: "resize-y",
      both: "resize"
    }
  }
});
const Textarea = forwardRef(
  ({
    name,
    fullWidth = false,
    textareaSize = "md",
    dense = false,
    label = "",
    classNames,
    radius = "none",
    WrapperComponent = "div",
    rows = 4,
    resizable = "vertical",
    hint,
    HintComponent = "div",
    hintId,
    startAdornment = null,
    endAdornment = null,
    ...props
  }, ref) => /* @__PURE__ */ jsxs(WrapperComponent, { className: cn("inline-block", fullWidth && "w-full", classNames == null ? void 0 : classNames.wrapper), children: [
    label && /* @__PURE__ */ jsxs("label", { className: cn("mb-1 block text-sm", classNames == null ? void 0 : classNames.label), htmlFor: name, children: [
      label,
      " ",
      props.required && "*"
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "relative w-full", children: [
      startAdornment,
      /* @__PURE__ */ jsx(
        "textarea",
        {
          ref,
          name,
          className: cn(
            textareaStyle({ radius, dense, size: textareaSize, resizable }),
            props.disabled && "opacity-50",
            classNames == null ? void 0 : classNames.textarea
          ),
          rows,
          "aria-describedby": hintId,
          ...props
        }
      ),
      endAdornment
    ] }),
    hint && /* @__PURE__ */ jsx(HintComponent, { id: hintId, className: cn("mt-1 text-xs opacity-75", classNames == null ? void 0 : classNames.hint), children: hint })
  ] })
);
const variants = {
  h1: "text-4xl",
  h2: "text-3xl",
  h3: "text-2xl",
  h4: "text-xl",
  h5: "text-lg",
  h6: "text-base",
  body1: "text-base",
  body2: "text-sm",
  body3: "text-xs",
  caption: "text-xs"
};
const Typography = ({ variant = "body1", children, className, style, name }) => {
  const Component = ["h1", "h2", "h3", "h4", "h5", "h6"].includes(variant == null ? void 0 : variant.toLowerCase()) ? variant : "p";
  return /* @__PURE__ */ jsx(
    Component,
    {
      name,
      className: cn(variants[variant], "text-neutral dark:text-neutral-dark", className),
      style,
      children
    }
  );
};
export {
  Accordion,
  Badge,
  Breadcrumbs,
  Button$1 as Button,
  Card,
  Carousel,
  Checkbox,
  Dialog,
  Divider,
  Drawer,
  Hero,
  Input$2 as Input,
  Loader,
  Menu,
  Pagination,
  Quantity,
  Radio,
  RawButton,
  Search,
  Select,
  Slider,
  Splash,
  Tab,
  Tabs,
  Textarea,
  Typography,
  useSlider
};
